import React, { useEffect, useRef } from 'react';
import { ArrowRight, Bot, Workflow, UserCheck, CheckCircle2 } from 'lucide-react';
import { useMagnetic } from '../hooks/useMagnetic';
import '../styles/Hero.css';

const scrollTo = (id: string) => (e: React.MouseEvent<HTMLElement>) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

const Hero: React.FC = () => {
  const magneticBtn = useMagnetic(10);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    const handle = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${e.clientX - r.left}px`);
      el.style.setProperty('--my', `${e.clientY - r.top}px`);
    };
    el.addEventListener('mousemove', handle);
    return () => el.removeEventListener('mousemove', handle);
  }, []);

  return (
    <section id="home" className="hero" ref={heroRef}>
      <div className="hero-grid" aria-hidden="true" />
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-spotlight" aria-hidden="true" />

      <div className="container hero-inner">
        <div className="hero-copy">
          <span className="eyebrow">
            Agentic Operations Platform
          </span>
          <h1>
            AI that doesn&rsquo;t answer—
            <br />
            <span className="gradient-text">it does the work.</span>
          </h1>
          <p className="hero-subtitle">
            Datryx designs and deploys autonomous agents and automation pipelines that run
            your operations end-to-end: customer support, sales, HR, data, documents, and
            the workflows unique to your business.
          </p>

          <div className="hero-buttons">
            <button
              ref={magneticBtn}
              className="btn-primary"
              onClick={scrollTo('capabilities')}
            >
              Explore capabilities
              <ArrowRight size={16} />
            </button>
          </div>

          <ul className="hero-chips">
            <li><CheckCircle2 size={14} /> Human-in-the-loop by default</li>
            <li><CheckCircle2 size={14} /> Integrates with your stack</li>
            <li><CheckCircle2 size={14} /> Ship in weeks, not quarters</li>
          </ul>
        </div>

        <div className="hero-visual" aria-hidden="true">
          <div className="preview-card">
            <div className="preview-chrome">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
              <span className="preview-title">datryx · agent.run</span>
            </div>

            <div className="preview-body">
              <div className="preview-row">
                <div className="row-icon"><Bot size={14} /></div>
                <div className="row-content">
                  <div className="row-title">Support Agent</div>
                  <div className="row-sub">Triaged 312 tickets · 96% self-served</div>
                </div>
                <span className="row-status status-ok">Live</span>
              </div>

              <div className="preview-row">
                <div className="row-icon row-icon-violet"><Workflow size={14} /></div>
                <div className="row-content">
                  <div className="row-title">Invoice Pipeline</div>
                  <div className="row-sub">OCR → validate → Xero · 1,204 processed</div>
                </div>
                <span className="row-status status-ok">Live</span>
              </div>

              <div className="preview-row">
                <div className="row-icon row-icon-cyan"><UserCheck size={14} /></div>
                <div className="row-content">
                  <div className="row-title">Onboarding Flow</div>
                  <div className="row-sub">New hire checklist · 8 steps automated</div>
                </div>
                <span className="row-status status-run">Running</span>
              </div>

              <div className="preview-chart">
                <div className="chart-label">
                  <span>Hours saved this week</span>
                  <span className="chart-value gradient-text">+1,284h</span>
                </div>
                <div className="chart-bars">
                  {[40, 62, 55, 78, 68, 88, 95].map((h, i) => (
                    <div key={i} className="bar" style={{ height: `${h}%`, animationDelay: `${i * 0.08}s` }} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="floating-card floating-a">
            <div className="floating-label">Agent action</div>
            <div className="floating-body">Routed lead → AE · Enriched via Clearbit</div>
          </div>

          <div className="floating-card floating-b">
            <div className="floating-label">Saved</div>
            <div className="floating-body">2.4h of manual QA</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
